import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { toDisplayError, type DisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';

const loginSchema = z.object({
  email: z.string().min(1, "L'adresse e-mail est requise.").email('Adresse e-mail invalide.'),
  password: z.string().min(1, 'Le mot de passe est requis.'),
  twoFactorCode: z.string().optional(),
});
type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [serverError, setServerError] = useState<DisplayError | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', twoFactorCode: '' },
  });

  // Défaut '/' : la page d'accueil ROUTE selon le rôle (client → portail, interne → back-office).
  const redirectTarget = (location.state as { from?: string } | null)?.from ?? '/';

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await login({
        email: values.email,
        password: values.password,
        twoFactorCode: values.twoFactorCode ? values.twoFactorCode : undefined,
      });
      navigate(redirectTarget, { replace: true });
    } catch (error) {
      // The server signals a required second factor with HTTP 401 +
      // message 'two_factor_required'. We reveal the TOTP field and let the user
      // resubmit — no business logic is duplicated client-side.
      const display = toDisplayError(error);
      if (display.message === 'two_factor_required') {
        setTwoFactorRequired(true);
        setServerError({
          message: 'Veuillez saisir votre code d’authentification à deux facteurs.',
        });
        return;
      }
      setServerError(display);
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Connexion</CardTitle>
          <p className="mt-1 text-sm text-slate-500">Plateforme documentaire DERLIZ</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            {serverError ? (
              <Alert variant="error" details={serverError.details}>
                {serverError.message}
              </Alert>
            ) : null}

            <div>
              <Label htmlFor="email">Adresse e-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                aria-invalid={Boolean(errors.email)}
                {...register('email')}
              />
              {errors.email ? (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              ) : null}
            </div>

            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={Boolean(errors.password)}
                {...register('password')}
              />
              {errors.password ? (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              ) : null}
            </div>

            {twoFactorRequired ? (
              <div>
                <Label htmlFor="twoFactorCode">Code d’authentification (2FA)</Label>
                <Input
                  id="twoFactorCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  {...register('twoFactorCode')}
                />
              </div>
            ) : null}

            <Button type="submit" className="w-full" loading={isSubmitting}>
              Se connecter
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
